import React from 'react';

import { css } from '@emotion/react';
import { BentoProvider } from '@robinhood/design';
import { CssVars, bankingTheme } from '@robinhood/design/product/themes';
import { GetServerSideProps } from 'next';

import AlertModal from 'src/components/creditApplication/AlertModal';
import DebugMenu from 'src/components/creditApplication/DebugMenu';
import {
  CkBadgeCookieKey,
  CkSubIdCookieKey,
  NavigationContext,
  NavigationProvider,
  PageComponentMap,
  X1InviteCookieKey,
} from 'src/components/creditApplication/NavigationProvider';
import { closeGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import LoadingPage from 'src/views/creditApplication/LoadingPage';
import UnknownErrorPage from 'src/views/creditApplication/UnknownErrorPage';

const ApplicationPage = () => {
  const { currentPage } = React.useContext(NavigationContext);
  const PageComponent = currentPage != null ? (PageComponentMap[currentPage] ?? UnknownErrorPage) : LoadingPage;
  const globalErrorAlertOpen = useGlobalStore((s) => s.globalErrorAlertOpen);

  return (
    <BentoProvider defaultToastDuration={5000} linkComponent={null} theme={bankingTheme}>
      {({ rootClassName: bentoClass }) => (
        <div className={bentoClass} css={root}>
          <div css={body}>
            <PageComponent />
            <AlertModal
              title='Error'
              description={'An unexpected error occurred. Please try again later.'}
              visible={globalErrorAlertOpen}
              onPrimaryButtonClick={closeGlobalErrorAlert}
              onClose={closeGlobalErrorAlert}
            />
          </div>
          <DebugMenu />
        </div>
      )}
    </BentoProvider>
  );
};

const WithProvider = () => {
  return (
    <div css={{ height: '100vh' }}>
      <NavigationProvider>
        <ApplicationPage />
      </NavigationProvider>
    </div>
  );
};

const root = css`
  display: flex;
  min-height: 100vh;
  width: 100vw;
  flex-direction: column;
  position: relative;
  background-color: ${CssVars.neutral.bg1};
`;

const body = css`
  flex-grow: 1;
  overflow-y: auto;
  padding-bottom: 60px;
  background-color: ${CssVars.neutral.bg1};
`;

const QueryParamToCookieKey = {
  cksubid: CkSubIdCookieKey,
  ckbadge: CkBadgeCookieKey,
  x1invite: X1InviteCookieKey,
};
const CookieMaxAge = 7 * 24 * 60 * 60; // 7 days in seconds

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { res, query } = context;
  const processedParams: string[] = [];

  Object.entries(QueryParamToCookieKey).forEach(([paramName, cookieKey]) => {
    const paramValue = query[paramName];
    if (!!paramValue && typeof paramValue === 'string') {
      res.appendHeader(
        'Set-Cookie',
        `${cookieKey}=${paramValue}; Path=/; Max-Age=${CookieMaxAge}; Secure; SameSite=Strict`,
      );
      processedParams.push(paramName);
    }
  });

  if (processedParams.length > 0) {
    const cleanQuery = { ...query };
    processedParams.forEach((param) => delete cleanQuery[param]);

    const searchParams = new URLSearchParams(cleanQuery as Record<string, string>);
    const cleanUrl = !!searchParams.toString() ? `/application?${searchParams.toString()}` : '/application';

    return {
      redirect: {
        destination: cleanUrl,
        permanent: false,
      },
    };
  }

  return {
    props: {},
  };
};

export default WithProvider;
